/**
 * Policy authoring API client — wraps /v1/admin/policies/*.
 *
 * The API is PATH-PER-TIER on purpose: the tier is a route to gate, not a
 * request field to validate. So `scope_id` is never in a body here either —
 * it is a path segment, and for the company tier it is not sent at all
 * (the server reads the caller's company from the request context).
 */

import { RestClient } from "./restClient";

/** The five scope levels, broadest first. A turn is subject to the UNION. */
export type PolicyTier =
  "platform" | "industry" | "company" | "department" | "user";

/** Which boundary a policy runs on. `both` is a rule checking prompt and reply. */
export type PolicyPhase = "pre" | "post" | "both";

export type PolicyKind = "rule" | "judge";

/** Deterministic patterns. Runs on both boundaries and CAN prevent output. */
export interface RuleDefinition {
  patterns: string[];
  action: "block" | "redact";
}

/** An LLM verdict. Advisory — on a reply it runs after the text has streamed. */
export interface JudgeDefinition {
  instruction: string;
  action: "block" | "flag";
}

export type PolicyDefinition = RuleDefinition | JudgeDefinition;

export function isRuleDefinition(d: PolicyDefinition): d is RuleDefinition {
  return "patterns" in d;
}

/** One authored row. `enabled: false` PARKS a rule — the preferred reversible
 *  removal, because a delete loses `updated_by`. */
export interface PolicyRow {
  scope_tier: PolicyTier;
  scope_id: string;
  key: string;
  phase: PolicyPhase;
  kind: PolicyKind;
  definition: PolicyDefinition;
  enabled: boolean;
  created_at: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface PolicyList {
  items: PolicyRow[];
  total: number;
  page: number;
  limit: number;
}

/** A row in the resolved union. `inherited` is true when the row's tier is
 *  BROADER than the queried scope. Two rows sharing a key both appear, and
 *  that is the union working — never a conflict. */
export interface EffectivePolicyRow extends PolicyRow {
  tier: PolicyTier;
  inherited: boolean;
}

export interface EffectivePolicyList {
  items: EffectivePolicyRow[];
}

/**
 * A partial update with THREE states per field, matching the `__unset__`
 * sentinel the server speaks:
 *   - field absent        → leave it alone
 *   - `definition: null`  → DELETE the row, so the scope re-inherits
 *   - a value             → write it
 * Hence `definition?: PolicyDefinition | null` and not an optional value.
 */
export interface PolicyUpsert {
  key: string;
  phase?: PolicyPhase;
  kind?: PolicyKind;
  definition?: PolicyDefinition | null;
  enabled?: boolean;
}

/** Addresses one writable scope. `scopeId` is required for every tier but
 *  `platform` (a singleton) and `company` (taken from the caller's context). */
export interface PolicyScope {
  tier: PolicyTier;
  scopeId?: string;
}

export interface PreviewRequest {
  text: string;
  scope_tier: PolicyTier;
  scope_id: string;
  phase: PolicyPhase;
}

export interface PreviewResult {
  action: "allow" | "block" | "redact";
  /** The rewritten text when the action is `redact`; the input otherwise. */
  text: string;
  /** The deciding `(tier, key)` pairs, in the engine's own order. */
  matched: [string, string][];
  /** FALSE means a pattern is wider than the streaming hold window, so it
   *  silently never matches across a chunk boundary. Load-bearing: it is the
   *  only moment the author can act on it. */
  stream_safe: boolean;
  /** One entry per pattern too wide for the hold window. The server sends
   *  `stream_warnings` (objects, not strings) — an earlier draft of this type
   *  said `warnings: string[]`, which typechecked fine because the response is
   *  unvalidated JSON and then crashed the page on `.length` of undefined. */
  stream_warnings: PreviewWarning[];
  /** The window a pattern must fit inside to be catchable mid-stream. */
  hold_window_chars: number;
}

export interface PreviewWarning {
  scope_tier: string;
  scope_id: string;
  key: string;
  pattern: string;
  needs_chars: number;
  hold_window_chars: number;
  detail: string;
}

/** One row of the industry vocabulary — the codes an industry-tier policy can
 *  be scoped to. Served from `egpt_platform_industries`, not a constant in this
 *  file: a second copy is how "construction" came to be valid in the database
 *  and refused by the application. */
export interface Industry {
  code: string;
  display_name: string;
  description: string;
}

function scopePath(scope: PolicyScope): string {
  const base = "/v1/admin/policies";
  switch (scope.tier) {
    case "platform":
      return `${base}/platform`;
    case "company":
      // No scope id: a company admin writes their OWN company, resolved
      // server-side from the session. Sending one would make it forgeable.
      return `${base}/company`;
    default:
      if (!scope.scopeId) {
        throw new Error(`policy scope "${scope.tier}" requires a scope id`);
      }
      return `${base}/${scope.tier}/${encodeURIComponent(scope.scopeId)}`;
  }
}

export class PoliciesApi {
  constructor(private client: RestClient) {}

  async list(scope: PolicyScope): Promise<PolicyList> {
    return this.client.get<PolicyList>(scopePath(scope));
  }

  /** Write one row. Returns the RECOMPUTED list for that scope, so a caller
   *  never needs a follow-up read to stay consistent. */
  async upsert(scope: PolicyScope, body: PolicyUpsert): Promise<PolicyList> {
    // The wire shape is `{policies: {<key>: {...}}}` — the key addresses the
    // row, it is not a field of it. Sent flat, the server's `PolicyBody`
    // ignores every extra field and answers 200 OK having written nothing:
    // no row, no audit line, and a UI that reports success over an empty
    // table. Keep the key out of the value.
    const { key, ...fields } = body;
    return this.client.put<PolicyList>(scopePath(scope), {
      policies: { [key]: fields },
    });
  }

  /** Park or unpark a rule without resending its definition — the fields left
   *  out are left alone, which is what preserves the authored definition. */
  async setEnabled(
    scope: PolicyScope,
    key: string,
    enabled: boolean,
  ): Promise<PolicyList> {
    return this.upsert(scope, { key, enabled });
  }

  /** Delete the row so this scope re-inherits. Prefer `setEnabled(false)`:
   *  a delete loses `updated_by`. */
  async remove(scope: PolicyScope, key: string): Promise<PolicyList> {
    // A null MAP VALUE is the delete verb. `definition: null` inside the body
    // is not — that is a PolicyBody missing its required phase/kind, which the
    // server rejects. So this cannot go through `upsert`.
    return this.client.put<PolicyList>(scopePath(scope), {
      policies: { [key]: null },
    });
  }

  /** The resolved union for a tenant — "what is this scope subject to right
   *  now", across all five levels. */
  async effective(params: {
    companyId?: string;
    deptId?: string;
    userId?: string;
  }): Promise<EffectivePolicyList> {
    const q = new URLSearchParams();
    if (params.companyId) q.set("company_id", params.companyId);
    if (params.deptId) q.set("dept_id", params.deptId);
    if (params.userId) q.set("user_id", params.userId);
    const query = q.toString();
    const resp = await this.client.get<{ items: PolicyRow[] }>(
      `/v1/admin/policies/effective${query ? `?${query}` : ""}`,
    );
    // The wire carries `scope_tier` and no `inherited` flag. Deriving both here
    // keeps the shape mismatch in the one place that owns the wire format —
    // reading `row.tier` in the view made `TIER_LABEL[undefined]`, so the union
    // sentence rendered "undefined requires redact". Same fix as
    // `companyPoliciesApi.listEffective`.
    return {
      items: (resp.items ?? []).map((row) => ({
        ...row,
        tier: row.scope_tier as PolicyTier,
        inherited: row.scope_tier !== "company",
      })),
    };
  }

  /** Dry-run text through the same evaluator a turn uses. */
  /** The industry vocabulary, for the picker. */
  async listIndustries(): Promise<Industry[]> {
    const resp = await this.client.get<{ items: Industry[] }>(
      "/v1/admin/policies/industries",
    );
    return resp.items ?? [];
  }

  /** Add an industry. Platform admin only; the server enforces it. */
  async createIndustry(industry: {
    code: string;
    display_name: string;
    description?: string;
  }): Promise<void> {
    await this.client.post("/v1/admin/policies/industries", industry);
  }

  async preview(body: PreviewRequest): Promise<PreviewResult> {
    return this.client.post<PreviewResult>("/v1/admin/policies/preview", body);
  }
}
